import { useCallback, useState, useTransition } from 'react'

import { toaster } from '@/components/ui'
import { ERROR_MESSAGES } from '@/constants/errorMessages'

import { CacheKey } from './cacheKey'
import { ApiError } from './error'
import { mutate } from './mutate'

export type UseMutationOptions<TResult> = {
  onSuccess?: (data: TResult) => void
  onError?: (error: ApiError) => void
  /** 処理成功後に更新するCacheを指定 */
  cacheKeysOnSuccess?: CacheKey[]
}

// 引数がない場合もあるので、可変長引数として定義
type MutationFn<TVariables extends unknown[], TResult> = (
  ...variables: TVariables
) => Promise<TResult>

export const useMutation = <TVariables extends unknown[], TResult>(
  mutationFn: MutationFn<TVariables, TResult>,
  options?: UseMutationOptions<TResult>,
) => {
  const [isPending, startTransition] = useTransition()
  const [data, setData] = useState<TResult | null>(null)
  const [error, setError] = useState<ApiError | null>(null)

  const request = useCallback(
    async (...variables: TVariables) => {
      startTransition(async () => {
        try {
          const result = await mutationFn(...variables)

          const cacheKeys = options?.cacheKeysOnSuccess
          if (cacheKeys) {
            cacheKeys.forEach(key => {
              mutate(key)
            })
          }
          setData(result)
          setError(null)
          options?.onSuccess?.(result)
        } catch (err) {
          const apiError =
            err instanceof ApiError
              ? err
              : new ApiError(ERROR_MESSAGES.defaultApiError)
          setError(apiError)
          options?.onError?.(apiError)
          // onErrorが指定されていない場合、デフォルトでtoastを表示する
          if (!options?.onError) {
            toaster.create({
              title: apiError.message,
              type: 'error',
            })
          }
        }
      })
    },
    [mutationFn, options],
  )

  return { request, isPending, data, error }
}
